import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, ArrowRight, ChevronDown, ChevronRight, Shield, EyeOff, FileText, Unplug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

interface GmailImportSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnect: (settings: ImportSettings) => void;
}

interface ImportSettings {
  periodMonths: number;
  maxSizeMb: number;
  smartImport: boolean;
  excludeNewsletters: boolean;
  attachmentsOnly: boolean;
}

// Preview documents matching actual app design
const previewDocuments = [
  { 
    emoji: "💸", 
    title: "Facture EDF", 
    type: "Facture",
    category: "Logement",
    categoryColor: "bg-blue-100 text-blue-800",
    date: "15 jan" 
  },
  { 
    emoji: "🛡️", 
    title: "Attestation Assurance Auto", 
    type: "Assurance",
    category: "Véhicule",
    categoryColor: "bg-orange-100 text-orange-800",
    date: "8 déc" 
  },
  { 
    emoji: "🏦", 
    title: "Relevé Compte Courant", 
    type: "Relevé",
    category: "Banque",
    categoryColor: "bg-yellow-100 text-yellow-800",
    date: "1 fév" 
  },
  { 
    emoji: "💶", 
    title: "Bulletin de Salaire", 
    type: "Salaire",
    category: "Travail",
    categoryColor: "bg-gray-100 text-gray-800",
    date: "28 jan" 
  },
];

export function GmailImportSettings({ open, onOpenChange, onConnect }: GmailImportSettingsProps) {
  const [settings, setSettings] = useState<ImportSettings>({
    periodMonths: 12,
    maxSizeMb: 25,
    smartImport: true,
    excludeNewsletters: true,
    attachmentsOnly: true,
  });
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const handleConnect = () => {
    onConnect(settings);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 gap-0 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <DialogHeader className="p-5 pb-4 border-b bg-gradient-to-b from-blue-50/50 to-transparent">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold">
            <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center shadow-sm">
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </div>
            <div>
              <span>Importer depuis Gmail</span>
              <p className="text-xs font-normal text-muted-foreground mt-0.5">
                Vos documents classés automatiquement
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Preview Section - Matching app design */}
        <div className="px-5 pt-4 pb-3">
          <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            Exemple de documents que nous allons retrouver
          </p>
          
          {/* Documents list matching RecentDocuments style */}
          <div className="space-y-1.5 rounded-xl border border-border/50 bg-muted/20 p-2">
            {previewDocuments.map((doc, i) => (
              <motion.div
                key={doc.title}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="flex items-center gap-2.5 p-2 rounded-lg bg-background hover:bg-secondary/50 transition-colors cursor-default group"
              >
                {/* Emoji icon box - matching app style */}
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 text-sm">
                  {doc.emoji}
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-xs truncate">{doc.title}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={cn(
                      "text-[9px] px-1.5 py-0.5 rounded-full font-medium",
                      doc.categoryColor
                    )}>
                      {doc.category}
                    </span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0 h-auto border-border/50">
                      {doc.type}
                    </Badge>
                    <span className="text-[9px] text-muted-foreground">
                      {doc.date}
                    </span>
                  </div>
                </div>
                
                {/* Chevron */}
                <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0 group-hover:text-muted-foreground transition-colors" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Security Section - Human friendly */}
        <div className="mx-5 p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Shield className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-emerald-900">Votre vie privée est protégée</p>
              <ul className="space-y-0.5">
                <li className="text-[11px] text-emerald-700 flex items-center gap-1.5">
                  <EyeOff className="w-3 h-3" />
                  Nous ne lisons jamais vos emails
                </li>
                <li className="text-[11px] text-emerald-700 flex items-center gap-1.5">
                  <FileText className="w-3 h-3" />
                  Seules les pièces jointes sont importées
                </li>
                <li className="text-[11px] text-emerald-700 flex items-center gap-1.5">
                  <Unplug className="w-3 h-3" />
                  Déconnectez-vous quand vous voulez
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Advanced Settings - Collapsible */}
        <div className="px-5 py-3">
          <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <span className="font-medium">Paramètres avancés</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${advancedOpen ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <AnimatePresence>
                {advancedOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 pt-3 pb-1"
                  >
                    {/* Period */}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">Période d'import</Label>
                      <Select 
                        value={settings.periodMonths.toString()} 
                        onValueChange={(v) => setSettings(s => ({ ...s, periodMonths: parseInt(v) }))}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3">3 derniers mois</SelectItem>
                          <SelectItem value="6">6 derniers mois</SelectItem>
                          <SelectItem value="12">12 derniers mois</SelectItem>
                          <SelectItem value="24">2 dernières années</SelectItem>
                          <SelectItem value="60">5 dernières années</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Max Size */}
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-muted-foreground">Taille max. par fichier</Label>
                      <Select 
                        value={settings.maxSizeMb.toString()} 
                        onValueChange={(v) => setSettings(s => ({ ...s, maxSizeMb: parseInt(v) }))}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 MB</SelectItem>
                          <SelectItem value="25">25 MB</SelectItem>
                          <SelectItem value="50">50 MB</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Smart Options */}
                    <div className="space-y-3 pt-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-xs font-medium">Import intelligent</Label>
                          <p className="text-[10px] text-muted-foreground">Garde seulement les documents importants</p>
                        </div>
                        <Switch 
                          checked={settings.smartImport}
                          onCheckedChange={(v) => setSettings(s => ({ ...s, smartImport: v }))}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-xs font-medium">Exclure les newsletters</Label>
                          <p className="text-[10px] text-muted-foreground">Ignore les emails marketing</p>
                        </div>
                        <Switch 
                          checked={settings.excludeNewsletters}
                          onCheckedChange={(v) => setSettings(s => ({ ...s, excludeNewsletters: v }))}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-xs font-medium">Pièces jointes uniquement</Label>
                          <p className="text-[10px] text-muted-foreground">PDF, images, documents...</p>
                        </div>
                        <Switch 
                          checked={settings.attachmentsOnly}
                          onCheckedChange={(v) => setSettings(s => ({ ...s, attachmentsOnly: v }))}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Footer with prominent CTA */}
        <div className="p-5 pt-2 border-t bg-muted/10">
          <Button 
            onClick={handleConnect}
            size="lg"
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all"
          >
            <Mail className="w-5 h-5 mr-2" />
            Connecter Gmail
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="text-[11px] text-center text-muted-foreground mt-3">
            Accès en lecture seule • Révocable à tout moment
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
